import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Input,
  Label,
  Alert,
  Badge,
} from '@/components/ui'
import { api, type SubdomainConfig, type SubdomainStatusResponse } from '@/api/client'
import { Loader2, Globe, RefreshCw, ExternalLink, CheckCircle, AlertCircle, Clock } from 'lucide-react'

function StatusBadge({ status, sslStatus }: { status: string; sslStatus: string }) {
  if (status === 'active' && sslStatus === 'active') {
    return (
      <Badge variant="success" className="flex items-center gap-1">
        <CheckCircle className="w-3 h-3" />
        Active
      </Badge>
    )
  }
  if (status === 'pending' || status === 'pending_validation' || sslStatus === 'pending_validation' || sslStatus === 'pending_issuance') {
    return (
      <Badge variant="warning" className="flex items-center gap-1">
        <Clock className="w-3 h-3" />
        Provisioning
      </Badge>
    )
  }
  if (status === 'failed' || status === 'not_found') {
    return (
      <Badge variant="error" className="flex items-center gap-1">
        <AlertCircle className="w-3 h-3" />
        Failed
      </Badge>
    )
  }
  return (
    <Badge variant="secondary" className="flex items-center gap-1">
      <Clock className="w-3 h-3" />
      {status}
    </Badge>
  )
}

export function DomainsSettingsPage() {
  const { orgSlug } = useParams()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [reprovisioning, setReprovisioning] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  // Subdomain config
  const [subdomains, setSubdomains] = useState<SubdomainConfig>({
    core_subdomain: null,
    api_subdomain: null,
    account_subdomain: null,
  })

  // Provisioning status
  const [provisioningStatus, setProvisioningStatus] = useState<SubdomainStatusResponse | null>(null)

  // Form state (allows empty string for clearing)
  const [coreSubdomain, setCoreSubdomain] = useState('')
  const [apiSubdomain, setApiSubdomain] = useState('')
  const [accountSubdomain, setAccountSubdomain] = useState('')

  useEffect(() => {
    loadConfig()
  }, [orgSlug])

  const loadConfig = async () => {
    if (!orgSlug) return
    setLoading(true)
    setError(null)
    try {
      const [configResponse, statusResponse] = await Promise.all([
        api.orgs.getConfig(orgSlug),
        api.orgs.getSubdomainStatus(orgSlug).catch(() => null),
      ])
      setSubdomains(configResponse.subdomains || {
        core_subdomain: null,
        api_subdomain: null,
        account_subdomain: null,
      })
      setCoreSubdomain(configResponse.subdomains?.core_subdomain || '')
      setApiSubdomain(configResponse.subdomains?.api_subdomain || '')
      setAccountSubdomain(configResponse.subdomains?.account_subdomain || '')
      setProvisioningStatus(statusResponse)
    } catch (err) {
      console.error('Failed to load config:', err)
      setError('Failed to load domain settings')
    } finally {
      setLoading(false)
    }
  }

  const handleReprovision = async () => {
    if (!orgSlug) return
    setReprovisioning(true)
    setError(null)
    setSuccess(null)
    try {
      const result = await api.orgs.reprovisionSubdomains(orgSlug)
      if (result.success) {
        setSuccess('Hostnames provisioned successfully')
        // Reload status
        const status = await api.orgs.getSubdomainStatus(orgSlug).catch(() => null)
        setProvisioningStatus(status)
      } else {
        setError(result.message || 'Failed to provision hostnames')
      }
    } catch (err) {
      console.error('Failed to reprovision:', err)
      setError('Failed to provision hostnames')
    } finally {
      setReprovisioning(false)
    }
  }

  const handleSaveSubdomains = async () => {
    if (!orgSlug) return

    setSaving(true)
    setError(null)
    setSuccess(null)

    try {
      const result = await api.orgs.updateSubdomains(orgSlug, {
        core_subdomain: coreSubdomain || null,
        api_subdomain: apiSubdomain || null,
        account_subdomain: accountSubdomain || null,
      })
      setSubdomains(result)
      setSuccess('Subdomain settings saved successfully')
    } catch (err) {
      console.error('Failed to save subdomains:', err)
      setError('Failed to save subdomain settings')
    } finally {
      setSaving(false)
    }
  }

  const hasChanges =
    coreSubdomain !== (subdomains.core_subdomain || '') ||
    apiSubdomain !== (subdomains.api_subdomain || '') ||
    accountSubdomain !== (subdomains.account_subdomain || '')

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-[var(--text-secondary)]" />
      </div>
    )
  }

  return (
    <div className="space-y-6 max-w-2xl">
      {error && (
        <Alert variant="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Custom Subdomain Aliases */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5" />
            Custom Subdomain Aliases
          </CardTitle>
          <CardDescription>
            Customize the subdomain prefixes for your organization's services.
            By default, your services are available at:
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Default URLs Preview with Status */}
          <div className="p-4 bg-[var(--bg-elevated)] rounded-lg space-y-3">
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <span className="text-[var(--text-secondary)]">Login/Auth:</span>
                {provisioningStatus?.core && (
                  <StatusBadge
                    status={provisioningStatus.core.status}
                    sslStatus={provisioningStatus.core.ssl_status}
                  />
                )}
              </div>
              <code className="text-[var(--text-primary)]">
                {coreSubdomain || 'core'}.{orgSlug}.u.software
              </code>
            </div>
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <span className="text-[var(--text-secondary)]">API:</span>
                {provisioningStatus?.api && (
                  <StatusBadge
                    status={provisioningStatus.api.status}
                    sslStatus={provisioningStatus.api.ssl_status}
                  />
                )}
              </div>
              <code className="text-[var(--text-primary)]">
                {apiSubdomain || 'api'}.{orgSlug}.u.software
              </code>
            </div>
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <span className="text-[var(--text-secondary)]">Account Dashboard:</span>
                {provisioningStatus?.account && (
                  <StatusBadge
                    status={provisioningStatus.account.status}
                    sslStatus={provisioningStatus.account.ssl_status}
                  />
                )}
              </div>
              <code className="text-[var(--text-primary)]">
                {accountSubdomain || 'account'}.{orgSlug}.u.software
              </code>
            </div>

            {/* Provisioning Error/Info */}
            {provisioningStatus?.provisioning_status === 'failed' && (
              <div className="mt-2 p-2 bg-red-500/10 border border-red-500/20 rounded text-xs text-red-400">
                <strong>Provisioning failed:</strong> {provisioningStatus.provisioning_error || 'Unknown error'}
              </div>
            )}

            {/* Reprovision Button */}
            {(provisioningStatus?.provisioning_status === 'failed' || provisioningStatus?.provisioning_status === 'pending') && (
              <div className="mt-2 flex justify-end">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={handleReprovision}
                  loading={reprovisioning}
                >
                  <RefreshCw className="w-3 h-3 mr-1" />
                  Reprovision Hostnames
                </Button>
              </div>
            )}
          </div>

          {/* Subdomain Inputs */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="core-subdomain">Login/Auth Subdomain</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="core-subdomain"
                  value={coreSubdomain}
                  onChange={(e) => setCoreSubdomain(e.target.value.toLowerCase())}
                  placeholder="core"
                  className="max-w-[150px]"
                />
                <span className="text-[var(--text-secondary)]">.{orgSlug}.u.software</span>
              </div>
              <p className="text-xs text-[var(--text-muted)]">
                Example: "login" → login.{orgSlug}.u.software
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="api-subdomain">API Subdomain</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="api-subdomain"
                  value={apiSubdomain}
                  onChange={(e) => setApiSubdomain(e.target.value.toLowerCase())}
                  placeholder="api"
                  className="max-w-[150px]"
                />
                <span className="text-[var(--text-secondary)]">.{orgSlug}.u.software</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="account-subdomain">Account Dashboard Subdomain</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="account-subdomain"
                  value={accountSubdomain}
                  onChange={(e) => setAccountSubdomain(e.target.value.toLowerCase())}
                  placeholder="account"
                  className="max-w-[150px]"
                />
                <span className="text-[var(--text-secondary)]">.{orgSlug}.u.software</span>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-4 border-t border-[var(--border-subtle)]">
            <Button
              variant="secondary"
              onClick={loadConfig}
              disabled={saving}
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Reset
            </Button>
            <Button
              onClick={handleSaveSubdomains}
              loading={saving}
              disabled={!hasChanges}
            >
              Save Changes
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Custom Domains (Coming Soon) */}
      <Card className="opacity-60">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ExternalLink className="w-5 h-5" />
            Custom Domains
          </CardTitle>
          <CardDescription>
            Configure custom domains for a fully white-labeled experience.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="p-4 bg-[var(--bg-elevated)] rounded-lg text-center">
            <p className="text-[var(--text-secondary)]">
              Custom domain support is coming soon. You'll be able to use domains like{' '}
              <code className="text-[var(--text-primary)]">login.yourcompany.com</code> for your auth pages.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
